import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@app/shared/shared.module';
import { FormControl, FormGroup } from '@angular/forms';
import { SearchFilterPipe } from '@app/shared/pipes/search-filter.pipe';
import { ValidatorFunctions } from '@app/shared/util/validator-functions';
import { CourseService } from '../../services/course.service';
import { SaveRelatedCourseData } from '../../models/related-course-data.model';
import { CourseCommonFunctions } from '../../course-util/course-common-functions';

@Component({
    selector: 'app-add-related-course',
    imports: [CommonModule, SharedModule, SearchFilterPipe],
    templateUrl: './add-related-course.component.html',
    styleUrl: './add-related-course.component.css',
    changeDetection: ChangeDetectionStrategy.Eager
})
export class AddRelatedCourseComponent implements OnInit, OnChanges {
  @Input() pageType: string = 'add';
  @Input() relatedCourseJSONDetails: any = null;
  @Input() relatedCourseRefData: any = {};
  @Input() relatedCourseAjaxData: any = {};
  @Input() buttonType: string = '';
  @Input() relatedCourseMatData: any = null;
  @Input() relatedCourseId: any = null;
  @Output() loadRelatedCourseData = new EventEmitter<any>();
  relatedCourseForm = signal<FormGroup | null>(null);
  validatorFn = inject(ValidatorFunctions);
  courseService = inject(CourseService);
  courseCommonFn = inject(CourseCommonFunctions);
  relatedCourseFields = signal<any[]>([]);
  submitFlag = false;
  viewRelatedCourseData = signal<any>({});
  relatedCourseRefValues: any = null;
  selectedValues: any = {};

  ngOnInit(): void {
    this.getViewRelatedCourseData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['buttonType']?.currentValue)
      this.saveOrClearRelatedCourse(changes['buttonType'].currentValue);
  }

  getViewRelatedCourseData() {
    if (this.relatedCourseId)
      this.courseService.viewRelatedCourseData(this.relatedCourseMatData.institutionId, this.relatedCourseMatData.courseId, this.relatedCourseMatData.courseInstanceId, this.relatedCourseId)?.subscribe({
        next: (res: any) => {
          this.viewRelatedCourseData.set(res);
          this.createRelatedCourseForm();
        },
        error: (err: Error) => console.log('save related course data', err.message)
      })
    else
      this.createRelatedCourseForm();
  }

  createRelatedCourseForm() {
    if (this.relatedCourseJSONDetails?.fields?.length)
      this.relatedCourseFields.set(structuredClone(this.relatedCourseJSONDetails.fields));
    if (this.relatedCourseRefData)
      this.relatedCourseRefValues = { ...this.relatedCourseRefData };
    const formGroup: any = {};
    this.relatedCourseFields()?.forEach((field: any) => {
      let value: any = '';
      if (Object.keys(this.viewRelatedCourseData())?.length)
        value = this.getFieldValues(field);
      formGroup[field.fieldId] = new FormControl(value, this.addValidator(field));
    });
    this.relatedCourseForm.set(new FormGroup(formGroup));
    if (this.pageType !== 'add') {
      if (this.pageType === 'edit')
        this.getCourseInstanceList('load');
      this.relatedCourseFields().filter((fld: any) => !fld.linkedField).forEach((field: any) => {
        const value: any = this.relatedCourseForm()?.get(field.fieldId)?.value || '';
        this.enableFields(field, value);
      })
    }
    this.relatedCourseForm()?.valueChanges.subscribe(() => {
      this.buttonType = '';
      this.loadRelatedCourseData.emit('reset');
    })
  }

  addValidator(fieldData: any) {
    const fieldValidators = [...this.validatorFn.addValidators(fieldData)];
    if (fieldData.fieldType === 'AJAX')
      fieldValidators.push(this.validatorFn.ajaxValidator('name'));
    return fieldValidators;
  }

  displayFn(ajaxValue: any) {
    return ajaxValue?.name || '';
  }

  updatedRelatedCourseRef(selectedValue: any, linkedFieldData: any, fieldData: any) {
    if (selectedValue?.id && this.relatedCourseRefValues[linkedFieldData?.fieldId?.toLowerCase()]) {
      const qualTypeIds = this.relatedCourseRefData?.pathwaycountryqualificationtypeid?.filter((data: any) => {
        let checkFlag = true;
        if (linkedFieldData?.fieldId?.toLowerCase()?.includes('qualificationtype'))
          checkFlag = (data?.studyLevelId === this.relatedCourseForm()?.get('pathwayStudyLevelId')?.value?.id);
        return checkFlag && (selectedValue?.id === data[fieldData?.fieldId?.replace('pathwayQ', 'q')?.replace('pathwayS', 's')])
      }).map((type: any) => { return type[linkedFieldData?.fieldId?.replace('pathwayQ', 'q')?.replace('pathwayS', 's')] });
      this.relatedCourseRefValues[linkedFieldData?.fieldId?.toLowerCase()] = this.relatedCourseRefData[linkedFieldData?.fieldId?.toLowerCase()]?.filter((data: any) => qualTypeIds?.includes(data?.id));
    }
  }

  enableFields(fieldData: any, selectedValue?: any, from?: string) {
    const linkedFieldData = this.relatedCourseFields()?.find((fld: any) => fieldData?.fieldId === fld.linkedField);
    if (linkedFieldData) {
      this.updatedRelatedCourseRef(selectedValue, linkedFieldData, fieldData);
      linkedFieldData.readOnly = !selectedValue;
      linkedFieldData.mandatory = linkedFieldData.readOnly ? 'N' : 'Y';
      let value = '';
      this.relatedCourseForm()?.removeControl(linkedFieldData.fieldId);
      if (Object.keys(this.viewRelatedCourseData())?.length && from !== 'clear')
        value = this.getFieldValues(linkedFieldData);
      this.relatedCourseForm()?.addControl(linkedFieldData.fieldId, new FormControl(value, this.addValidator(linkedFieldData)));
      this.relatedCourseForm()?.get(linkedFieldData.fieldId)?.updateValueAndValidity();
      if (linkedFieldData.fieldId === 'pathwayCourseInstanceId' && linkedFieldData.readOnly && this.relatedCourseAjaxData?.pathwayCourseInstanceId)
        delete this.relatedCourseAjaxData?.pathwayCourseInstanceId;
      if (linkedFieldData?.linkedField)
        this.enableFields(linkedFieldData, value, from);
    }
  }

  getFieldValues(fieldData: any) {
    let value = '';
    const fieldName = fieldData.fieldId?.toLowerCase()?.includes('courseinstance') ? fieldData.fieldId?.replace('Id', 'Title') : fieldData.fieldId?.replace('Id', 'Name');
    value = this.viewRelatedCourseData()[fieldName] || '';
    if (fieldData.fieldType === 'SELECT_BOX' && this.pageType === 'edit' && this.relatedCourseRefValues[fieldData.fieldId?.toLowerCase()] && value)
      value = this.relatedCourseRefValues[fieldData.fieldId?.toLowerCase()]?.find((val: any) => val.name === value || val.id === value) || '';
    if (fieldData.fieldType === 'AJAX' && this.pageType === 'edit' && this.relatedCourseAjaxData[fieldData.fieldId] && value)
      value = this.relatedCourseAjaxData[fieldData.fieldId]?.find((val: any) => val.name === value || val.id === value) || '';
    return value;
  }

  checkFieldValues(event: any, fieldId?: string) {
    if (fieldId === 'pathwayCourseInstanceId') {
      this.loadRelatedCourseData.emit();
      if (this.relatedCourseForm()?.get('pathwayInstitutionId')?.invalid) {
        this.loadRelatedCourseData.emit({ warning: 'Please enter institution details before selecting the course instance title' });
        event?.stopPropagation();
      } else {
        this.getCourseInstanceList();
      }
    }
  }

  getCourseInstanceList(from?: string) {
    const formValue = this.relatedCourseForm()?.value;
    const filterDetails: any = {
      relatedCourseInstanceId: this.relatedCourseMatData.courseInstanceId,
      pathwayInstitutionId: (from) ? this.viewRelatedCourseData()?.pathwayInstitutionId : formValue?.pathwayInstitutionId?.id,
      qualificationTypeGroupId: formValue?.pathwayQualificationTypeGroupId?.id,
      qualificationTypeId: formValue?.pathwayQualificationTypeId?.id,
      qualificationStudyLevelId: formValue?.pathwayStudyLevelId?.id,
      countryId: this.relatedCourseMatData.countryId
    }
    if (this.pageType === 'edit' && from === 'load')
      filterDetails['attachedCourseInstanceId'] = this.viewRelatedCourseData()?.pathwayCourseInstanceId;

    const fieldNames = ['pathwayInstitutionId', 'qualificationTypeGroupId', 'qualificationTypeId', 'qualificationStudyLevelId'];
    let checkFlag = true;
    if (Object.keys(this.selectedValues).length)
      fieldNames.forEach((field: string) => {
        if (checkFlag)
          checkFlag = (this.selectedValues[field] === filterDetails[field]);
      })
    else
      checkFlag = false;
    if (checkFlag)
      return
    this.selectedValues = filterDetails;
    this.fetchCourseInstanceData(filterDetails, from)
  }

  fetchCourseInstanceData(filterDetails: any, from: any) {
    this.courseService.getCourseInstanceData(1, this.relatedCourseMatData.courseId, '', '', filterDetails, true)
      .subscribe({
        next: (res: any) => {
          if (res?.courseInstances)
            this.relatedCourseAjaxData['pathwayCourseInstanceId'] = res.courseInstances?.map((ins: any) => {
              return { id: ins.courseInstanceId, name: (ins.courseInstanceTitle + ' (' + ins.courseInstanceId + ')') }
            })?.filter((val: any) => val?.id != this.relatedCourseMatData?.courseInstanceId);
          if (from && this.relatedCourseAjaxData['pathwayCourseInstanceId']?.length) {
            const selectedValue = this.relatedCourseAjaxData['pathwayCourseInstanceId'].find((val: any) => val.id === this.viewRelatedCourseData()?.pathwayCourseInstanceId);
            if (selectedValue) this.relatedCourseForm()?.get('pathwayCourseInstanceId')?.setValue(selectedValue);
          }
        },
        error: (err: Error) => console.log(err.message)
      });
  }

  saveOrClearRelatedCourse(buttonType: string) {
    this.submitFlag = false;
    this.loadRelatedCourseData.emit('reset');
    if (buttonType === 'clear') {
      this.clearRelatedDetails();
    } else {
      this.submitFlag = this.relatedCourseForm()!.invalid;
      if (this.submitFlag)
        return
      const relatedCourseFormData = this.relatedCourseForm()!.value;
      const relatedCourseData: any = this.getSaveRelatedCourseData(relatedCourseFormData);
      if (this.pageType === 'add')
        this.addRelatedCourseDetails(relatedCourseFormData, relatedCourseData);
      else
        this.editRelatedCourseDetails(relatedCourseFormData, relatedCourseData);
    }
  }

  clearRelatedDetails() {
    this.relatedCourseFields().forEach((field: any) => {
      this.relatedCourseForm()?.get(field.fieldId)?.reset('');
      if (!field.linkedField)
        this.enableFields(field, '', 'clear');
    })
    this.relatedCourseForm()?.updateValueAndValidity();
  }

  addRelatedCourseDetails(relatedCourseFormData: any, relatedCourseData: any) {
    this.courseService.saveRelatedCourseData(this.relatedCourseMatData.institutionId, this.relatedCourseMatData.courseId, this.relatedCourseMatData.courseInstanceId, relatedCourseData)?.subscribe({
      next: (res: any) => {
        if (res?.pathwayCourseInstanceRelationshipId) {
          const successMsg = 'The related course ' + relatedCourseFormData.pathwayCourseInstanceId.name + ' is successfully added';
          this.loadRelatedCourseData.emit({ success: successMsg });
        }
      },
      error: (err: Error) => console.log('save related course data', err.message)
    })
  }

  editRelatedCourseDetails(relatedCourseFormData: any, relatedCourseData: any) {
    this.courseService.editRelatedCourseData(this.relatedCourseMatData.institutionId, this.relatedCourseMatData.courseId, this.relatedCourseMatData.courseInstanceId, this.relatedCourseId, relatedCourseData)?.subscribe({
      next: (res: any) => {
        if (res?.message === 'Successfull response') {
          const successMsg = 'The related course ' + relatedCourseFormData.pathwayCourseInstanceId.name + ' is successfully updated';
          this.loadRelatedCourseData.emit({ success: successMsg });
        }
      },
      error: (err: Error) => console.log('edit related course data', err.message)
    })
  }

  getSaveRelatedCourseData(relatedCourseFormData: any) {
    const relatedCourseData: any = new SaveRelatedCourseData;
    Object.keys(relatedCourseData)?.forEach((fieldId: string) => {
      if (relatedCourseFormData[fieldId])
        relatedCourseData[fieldId] = relatedCourseFormData[fieldId]?.id || relatedCourseFormData[fieldId];
    })
    this.courseCommonFn.getUserData(relatedCourseData, this.pageType);
    return relatedCourseData;
  }
}
